// Typed API client for the VerityRx dashboard. One axios instance attaches auth
// headers per mode (dev = X-User-Id/X-Role, prod = Authorization: Bearer). Every
// endpoint from the contract is a typed method here; screens/hooks never build URLs.

import axios, { type AxiosInstance } from 'axios';
import { createReadCache } from './readCache';
import { API_BASE, AUTH_MODE, getIdentity } from '../auth/identity';
import { capacityForRequest, releasePreview, selectedCapacity } from '../components/capacity';
import type { InviteSent } from '../components/inviteSent';
import type { ResultBreakdown } from '../shared/resultBreakdown';
import type {
  AdminUser,
  BulkDispositionResult,
  CommentInput,
  CompleteInput,
  CompletePrecheck,
  CompleteResult,
  CostDefaults,
  CostEstimate,
  CostEstimateInput,
  CreateCustomerResult,
  CreateSnapshotResult,
  CreateWorkspaceInput,
  DataSourceStatusList,
  StillNeededOperatorList,
  DispositionInput,
  GroupDetail,
  MyCustomer,
  FirmSummary,
  FirmMember,
  FirmRoster,
  FirmReferralPage,
  NoticeCandidateList,
  OpenItemsSummary,
  OperatorPartRequest,
  OperatorPracticeGroup,
  Verdict,
  NoticeDecisionKind,
  NoticeDetailView,
  NoticeQueuePage,
  NoticeReceipt,
  RemediationDoc,
  ResearchCustomerRequestQueue,
  ResearchDisposition,
  ResearchDraftDetail,
  ResearchDraftReview,
  ResearchDraftRow,
  ResearchImportResult,
  ResearchPublicationHistory,
  ResearchSourceChangeQueue,
  RuleGroupRow,
  SessionSummary,
  SitePreview,
  StateCoverage,
  StateFinding,
  StateRow,
  UploadReport,
  UploadTeamRow,
  UploadsTeamList,
  WorkspaceDetail,
  WorkspaceRow,
  AttentionPayload,
} from './types';

// Exported for the portal client (portal/api.ts) — same instance, same auth
// interceptors, so the two surfaces cannot drift on how requests are made.
// 120s matches App Runner's fixed request kill: without a client timeout a
// hung request span a spinner forever past the point the server had already
// given up (2026-09-01 review). The slowest legitimate call is the
// whole-site scan (~60-90s worst case), which fits.
export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 120_000,
});
// One shared read layer (in-flight dedupe + an 8 s cache of the stable read
// routes, dropped by every write): see readCache.ts.
http.defaults.adapter = createReadCache(axios.getAdapter(http.defaults.adapter));

// Attach auth on every request from the live identity (kept in sync by AuthProvider).
http.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  const match = window.location.pathname.match(/^\/(?:portal|c|customers|provider)\/([0-9a-f-]{36})(?:\/|$)/i);
  if (match) {
    const identityProbe = config.url?.split('?')[0] === '/me';
    const portalPreview = window.location.pathname.startsWith('/portal/') && releasePreview(match[1]);
    const capacity = identityProbe ? null : portalPreview ? 'practice_preview' :
      capacityForRequest(config.url, window.location.pathname, selectedCapacity(match[1]));
    if (capacity) config.headers['X-VerityRx-Capacity'] = capacity;
    else if (identityProbe) delete config.headers['X-VerityRx-Capacity'];
    if (portalPreview && !identityProbe) config.headers['X-VerityRx-Preview-Release'] = 'true';
  }
  const identity = getIdentity();
  if (AUTH_MODE === 'prod') {
    if (identity?.token) {
      config.headers.Authorization = `Bearer ${identity.token}`;
    }
  } else if (identity) {
    config.headers['X-User-Id'] = identity.userId;
    config.headers['X-Role'] = identity.role;
    if (identity.sessionKey) config.headers['X-Session-Key'] = identity.sessionKey;
  }
  return config;
});

export interface ApiError {
  status: number;
  detail: string;
}

export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const raw = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    // FastAPI's 422 detail is an ARRAY of error objects. Rendering that raw
    // threw "Objects are not valid as a React child" and, with no boundary
    // above the portal, white-screened the whole surface (2026-09-01
    // pre-demo review). Whatever shape arrives, the caller gets a string.
    let detail: string;
    if (typeof raw === 'string') {
      detail = raw;
    } else if (Array.isArray(raw)) {
      detail = raw
        .map((d) =>
          typeof d === 'object' && d !== null && 'msg' in d
            ? String((d as { msg: unknown }).msg)
            : String(d),
        )
        .join('; ');
    } else if (raw != null) {
      detail = JSON.stringify(raw);
    } else {
      detail = err.message;
    }
    return { status: err.response?.status ?? 0, detail };
  }
  return { status: 0, detail: String(err) };
}

export interface StateFindingFilters {
  status?: string;
  verdict?: Verdict;
}

/** Where an operator's "Build now" stands for the practice's current intake. */
export interface MedspaBuildState {
  available?: boolean;
  state: 'idle' | 'not_ready' | 'building' | 'built' | 'failed';
  counsel_rebuild_available?: boolean;
  rule_follow_up?: Array<{ subject: string; reason: string; checks?: number }>;
  counsel_feedback?: import('../customer/api').CounselFeedback | null;
  requested?: boolean;
  requested_at?: string;
  built_at?: string | null;
  reason?: string;
}

export interface PendingMedspaBinder {
  status: 'not_ready' | 'not_built' | 'pending';
  verdicts?: Record<'compliant' | 'grey' | 'non_compliant', number>;
  verdict_diff?: Record<'compliant' | 'grey' | 'non_compliant', number>;
  finding_diff?: { added: number; removed: number; changed_verdict: number };
  audit_warnings?: string[];
  audit_ok?: boolean;
  export_gate_ok?: boolean;
  preview_bytes_hash?: string;
  gate_problems?: string[];
  citation_checks_failed?: CitationCheckFailure[];
  current_version?: number | null;
  /** The names counsel and the team say ("v6"), one sequence per practice (BL-801). */
  binder_name?: string | null;
  current_binder_name?: string | null;
  /** Set once counsel has this exact binder: the panel then offers nothing (BL-802). */
  already_sent?: { binder_name: string | null; sent_at: string | null } | null;
  can_publish?: boolean;
  /** What each scope would give counsel (PM-27): the export's own counts. */
  scope_preview?: {
    review_obligations?: { needs_counsel: number; independent_attestation: number; awaiting_practice: number; awaiting_confirmation?: number; unknown: number };
    legal_judgement: number;
    settled: number;
    total: number;
    /** The settled count by who owns it (C3FOLLOW; the med-spa preview carries it like telehealth's). */
    settled_kinds?: { awaiting_practice: number; not_met: number; confirmations: number; compliant: number; not_classified?: number };
    /** CPO46D: the binder cover's own parts, the card's tiles. */
    breakdown?: ResultBreakdown;
  };
}

/** One cited source whose latest official-text check failed (BL-765). */
export interface CitationCheckFailure {
  title: string;
  url: string;
  status: 'NOT_FOUND' | 'UNREACHABLE' | string;
  as_of_date: string | null;
}

export interface ReleasePreview {
  results_held: boolean;
  /** CPO46D: the ONE breakdown the practice's page, the binder and counsel show. */
  counts: ResultBreakdown;
  total: number;
  recipients: string[];
}

/** One pilot feature's per-practice switch (FLAGS): on until the team turns it off. */
export interface FeatureSwitch {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  reason: string | null;
  changed_by: string | null;
  changed_at: string | null;
}

export const api = {
  async myFirms(): Promise<FirmSummary[]> {
    const { data } = await http.get<FirmSummary[]>('/firms'); return data;
  },
  async firmRoster(firmId: string, offset = 0): Promise<FirmRoster> {
    const { data } = await http.get<FirmRoster>(`/firms/${firmId}/roster`, { params: { offset } }); return data;
  },
  async firmMembers(firmId: string): Promise<FirmMember[]> {
    const { data } = await http.get<FirmMember[]>(`/firms/${firmId}/members`); return data;
  },
  async firmAssign(firmId: string, body: {customer_id: string; attorney_user_id: string; due_date: string | null}): Promise<void> {
    await http.put(`/firms/${firmId}/assignment`, body);
  },
  async firmReferral(firmId: string, body: {practice_name: string; contact_email: string; vertical: string}): Promise<void> {
    await http.post(`/firms/${firmId}/referrals`, body);
  },
  async firmReferrals(offset = 0): Promise<FirmReferralPage> {
    const { data } = await http.get<FirmReferralPage>('/firms/referrals', { params: { offset } }); return data;
  },
  async firmReferralHandled(requestId: string, customerId: string): Promise<void> {
    await http.post(`/firms/referrals/${requestId}/handled`, { customer_id: customerId });
  },
  // ---- Customer switcher ----------------------------------------------------
  async myCustomers(): Promise<MyCustomer[]> {
    const { data } = await http.get<MyCustomer[]>('/my-customers');
    return data;
  },

  // ---- User administration (internal-admin only) ---------------------------
  async adminUsers(): Promise<AdminUser[]> {
    const { data } = await http.get<AdminUser[]>('/users');
    return data;
  },

  async revokeAccess(customerId: string, accessId: string): Promise<void> {
    await http.post(`/customers/${customerId}/access/${accessId}/revoke`);
  },

  /** Full access or records only for a practice person (MEET R2), audited. */
  async changePracticeAccess(customerId: string, accessId: string, access: 'full' | 'records', admin = false): Promise<void> {
    await http.post(`/customers/${customerId}/access/${accessId}/change`, { access, admin });
  },

  async restoreAccess(customerId: string, accessId: string): Promise<void> {
    await http.post(`/customers/${customerId}/access/${accessId}/restore`);
  },

  async resendInvite(customerId: string, accessId: string): Promise<InviteSent | undefined> {
    const { data } = await http.post<{ sent?: InviteSent }>(
      `/customers/${customerId}/access/${accessId}/resend-invite`,
    );
    return data?.sent;
  },

  async disableUser(email: string): Promise<void> {
    await http.post('/users/disable', { email });
  },

  // ---- Session --------------------------------------------------------------
  async session(customerId: string, roundId?: string | null): Promise<SessionSummary> {
    const { data } = await http.get<SessionSummary>(
      `/customers/${customerId}/session`, { params: roundId ? { round_id: roundId } : undefined },
    );
    return data;
  },

  // ---- Rule-group surface (primary) ----------------------------------------
  async groups(customerId: string, sessionId: string): Promise<RuleGroupRow[]> {
    const { data } = await http.get<RuleGroupRow[]>(
      `/customers/${customerId}/session/${sessionId}/groups`,
    );
    return data;
  },

  async counselChangeBrief(customerId: string, sessionId: string): Promise<{
    available: boolean; reason?: string; from_version?: string; to_version?: string;
    groups?: Array<{ rule_group_key: string; label: string; change: string; words: string; cause: string }>;
    resolutions?: Array<{ rule_group_key: string; group: string; item: string; reason: string | null; result: string | null; resolution: string; resolution_words: string; cause: string }>;
    groups_to_read?: number; groups_last_round?: number;
    practice_facts?: Array<{ change: string; label: string }>;
  }> {
    const { data } = await http.get(`/customers/${customerId}/session/${sessionId}/change-brief`);
    return data;
  },

  async adoption(customerId: string, sessionId: string): Promise<{ eligible_decisions: number; eligible_by_group: Record<string, number>; prior_session_id: string | null }> {
    const { data } = await http.get(`/customers/${customerId}/session/${sessionId}/adoption`);
    return data;
  },

  async adoptDecisions(customerId: string, sessionId: string, groupKey?: string): Promise<{ adopted_count: number }> {
    const { data } = await http.post(`/customers/${customerId}/session/${sessionId}/adoption`, null,
      { params: groupKey ? { group_key: groupKey } : undefined });
    return data;
  },

  async recordGroupTime(customerId: string, sessionId: string, groupKey: string, seconds: number): Promise<void> {
    await http.post(`/customers/${customerId}/session/${sessionId}/groups/${encodeURIComponent(groupKey)}/time`, { seconds });
  },

  async signReview(customerId: string, sessionId: string, body: {
    typed_name: string; firm: string; bar_number: string; bar_state: string; scope_text: string; attests: boolean;
  }): Promise<{ attestation_id: string; signed_at: string; status: string; undecided: number }> {
    const { data } = await http.post(`/customers/${customerId}/session/${sessionId}/signature`, body);
    return data;
  },

  async group(
    customerId: string,
    sessionId: string,
    key: string,
  ): Promise<GroupDetail> {
    const { data } = await http.get<GroupDetail>(
      `/customers/${customerId}/session/${sessionId}/groups/${encodeURIComponent(key)}`,
    );
    return data;
  },

  async dispositionGroup(
    customerId: string,
    sessionId: string,
    key: string,
    verdict: Verdict,
    body: DispositionInput,
  ): Promise<BulkDispositionResult> {
    const { data } = await http.post<BulkDispositionResult>(
      `/customers/${customerId}/session/${sessionId}/groups/${encodeURIComponent(key)}/disposition?verdict=${verdict}`,
      body,
    );
    return data;
  },

  // "What we could not check" (LR-REPORT.md G-10a) — read-only, above the
  // Confirmations section, so a round can never be completed blind to it.
  async openItems(customerId: string, sessionId: string): Promise<OpenItemsSummary> {
    const { data } = await http.get<OpenItemsSummary>(
      `/customers/${customerId}/session/${sessionId}/open-items`,
    );
    return data;
  },

  // ---- State surface (secondary) -------------------------------------------
  async states(customerId: string, sessionId: string): Promise<StateRow[]> {
    const { data } = await http.get<StateRow[]>(
      `/customers/${customerId}/session/${sessionId}/states`,
    );
    return data;
  },

  async stateFindings(
    customerId: string,
    sessionId: string,
    state: string,
    filters: StateFindingFilters = {},
  ): Promise<StateFinding[]> {
    const { data } = await http.get<StateFinding[]>(
      `/customers/${customerId}/session/${sessionId}/states/${state}/findings`,
      { params: filters },
    );
    return data;
  },

  async dispositionState(
    customerId: string,
    sessionId: string,
    state: string,
    body: DispositionInput,
  ): Promise<BulkDispositionResult> {
    const { data } = await http.post<BulkDispositionResult>(
      `/customers/${customerId}/session/${sessionId}/states/${state}/disposition`,
      body,
    );
    return data;
  },

  // ---- Per-finding disposition ---------------------------------------------
  async dispositionFinding(
    customerId: string,
    sessionId: string,
    findingId: string,
    body: DispositionInput,
  ): Promise<{ ok: true }> {
    const { data } = await http.put<{ ok: true }>(
      `/customers/${customerId}/session/${sessionId}/findings/${findingId}/disposition`,
      body,
    );
    return data;
  },

  // ---- Comments -------------------------------------------------------------
  async addComment(
    customerId: string,
    findingId: string,
    body: CommentInput,
  ): Promise<{ comment_id: string }> {
    const { data } = await http.post<{ comment_id: string }>(
      `/customers/${customerId}/findings/${findingId}/comments`,
      body,
    );
    return data;
  },

  // ---- Complete review ------------------------------------------------------
  async completePrecheck(
    customerId: string,
    sessionId: string,
  ): Promise<CompletePrecheck> {
    const { data } = await http.get<CompletePrecheck>(
      `/customers/${customerId}/session/${sessionId}/complete/precheck`,
    );
    return data;
  },

  async complete(
    customerId: string,
    sessionId: string,
    body: CompleteInput,
  ): Promise<CompleteResult> {
    const { data } = await http.post<CompleteResult>(
      `/customers/${customerId}/session/${sessionId}/complete`,
      body,
    );
    return data;
  },

  // The feedback packet (internal-admin): what we pull to build the next version.
  async feedbackPacketUrl(customerId: string, sessionId: string): Promise<string> {
    const { data } = await http.get<Blob>(
      `/customers/${customerId}/session/${sessionId}/feedback-packet`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(data);
  },

  // The attorney's own durable record of a COMPLETED round (LR-REPORT.md
  // G-10f): every disposition they recorded plus the decision reference, as
  // a CSV. A blob URL, same pattern as the feedback packet — the request
  // needs the auth header this client attaches, so a bare <a href> would 401.
  async myDispositionsCsvUrl(customerId: string, sessionId: string): Promise<string> {
    const { data } = await http.get<Blob>(
      `/customers/${customerId}/session/${sessionId}/my-dispositions.csv`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(data);
  },

  // ---- Internal-admin only --------------------------------------------------
  async createCustomer(body: {
    name: string;
    vertical: string;
  }): Promise<CreateCustomerResult> {
    const { data } = await http.post<CreateCustomerResult>('/customers', body);
    return data;
  },

  async createSnapshot(
    customerId: string,
    exportFile: File,
    binderPdf?: File | null,
  ): Promise<CreateSnapshotResult> {
    // Upload the offline-built binder: the structured export drives the review UI;
    // the optional human-readable PDF is stored so the attorney can read the real
    // document (both produced offline — no runtime render).
    const form = new FormData();
    form.append('export', exportFile);
    if (binderPdf) form.append('binder_pdf', binderPdf);
    const { data } = await http.post<CreateSnapshotResult>(
      `/customers/${customerId}/snapshots`,
      form,
    );
    return data;
  },

  // Variant B (unreviewed, descriptive-only) remediation summary for the
  // customer's current snapshot. Read-only; nothing here sets a verdict.
  async remediation(customerId: string): Promise<RemediationDoc> {
    const { data } = await http.get<RemediationDoc>(
      `/customers/${customerId}/remediation`,
    );
    return data;
  },

  // A short-lived presigned URL for the session's uploaded binder PDF.
  async binderUrl(customerId: string, sessionId: string): Promise<string> {
    const { data } = await http.get<{ url: string }>(
      `/customers/${customerId}/session/${sessionId}/binder-url`,
    );
    return data.url;
  },

  async invite(
    customerId: string,
    body: { email: string; role: string; destination?: import('../auth/invitationDestination').InvitationDestination; assigned_prescriber_id?: string | null },
  ): Promise<{ sent?: InviteSent }> {
    const { data } = await http.post<{ sent?: InviteSent }>(
      `/customers/${customerId}/invite`,
      body,
    );
    return data;
  },

  async pendingMedspaBinder(customerId: string): Promise<PendingMedspaBinder> {
    const { data } = await http.get<PendingMedspaBinder>(`/operator/customers/${customerId}/binder/pending`);
    return data;
  },

  async medspaBuildStatus(customerId: string): Promise<MedspaBuildState> {
    const { data } = await http.get<MedspaBuildState>(`/operator/customers/${customerId}/binder/build-status`);
    return data;
  },

  async requestMedspaBuild(customerId: string, counsel = false): Promise<MedspaBuildState> {
    const { data } = await http.post<MedspaBuildState>(`/operator/customers/${customerId}/binder/build`, { with_counsel_decisions: counsel });
    return data;
  },

  async publishMedspaBinder(
    customerId: string,
    previewBytesHash: string,
    choice: { scope: 'legal_judgement' | 'whole_report'; settledConfirmed: boolean; closeOpenRounds?: boolean; wholeReason?: string } = { scope: 'whole_report', settledConfirmed: false },
  ): Promise<{ version: number; binder_label?: string | null; idempotent: boolean }> {
    const { data } = await http.post<{ version: number; binder_label?: string | null; idempotent: boolean }>(`/operator/customers/${customerId}/binder/publish`, {
      preview_bytes_hash: previewBytesHash,
      scope: choice.scope,
      settled_confirmed: choice.settledConfirmed,
      ...(choice.closeOpenRounds ? { close_open_rounds: true } : {}),
      ...(choice.wholeReason?.trim() ? { whole_report_reason: choice.wholeReason.trim() } : {}),
    });
    return data;
  },

  // ---- Operator console (internal-admin only; BL-344) ----------------------
  // Every one of these refuses a non-admin on the server with a 403.
  async workspaces(includeArchived = false, readyToBuild = false): Promise<WorkspaceRow[]> {
    const { data } = await http.get<WorkspaceRow[]>('/operator/customers', {
      params: { ...(includeArchived ? { include_archived: true } : {}), ...(readyToBuild ? { ready_to_build: true } : {}) },
    });
    return data;
  },

  async operatorAttention(): Promise<AttentionPayload> {
    const { data } = await http.get<AttentionPayload>('/operator/customers/attention');
    return data;
  },

  async resetDemoPractice(vertical: 'telehealth_rx' | 'medspa_service'):
    Promise<{ customer_id: string; vertical: string; reset: boolean; is_demo: true }> {
    const { data } = await http.post(`/operator/demo-practices/${vertical}/reset`, {});
    return data;
  },

  async workspace(customerId: string): Promise<WorkspaceDetail> {
    const { data } = await http.get<WorkspaceDetail>(`/operator/customers/${customerId}`);
    return data;
  },

  async recordReadinessJudgement(customerId: string, ready: boolean, reason: string): Promise<{ build_enabled?: boolean }> {
    const { data } = await http.post<{ build_enabled?: boolean }>(`/operator/customers/${customerId}/readiness-judgement`, { ready, reason });
    return data;
  },

  /** The team's summary of this practice's law-change notes (FXTEAMWIDE, PM-32). */
  async lawNoticesSummary<T>(customerId: string): Promise<T> {
    const { data } = await http.get<T>(`/operator/customers/${customerId}/law-notices-summary`);
    return data;
  },

  /** "Check readiness again": derived from the practice's own details (PM-14). */
  async recheckReadiness(customerId: string): Promise<{ ready: boolean }> {
    const { data } = await http.post<{ ready: boolean }>(`/operator/customers/${customerId}/readiness/recheck`);
    return data;
  },

  /** Log refused citations for the research team (PM-25). */
  async citationResearchStatus(customerId: string): Promise<{ requested_at: string | null; count: number }> {
    const { data } = await http.get<{ requested_at: string | null; count: number }>(`/operator/customers/${customerId}/citation-research-request`);
    return data;
  },

  async requestCitationResearch(customerId: string, citations: CitationCheckFailure[]): Promise<{ requested_at: string; count: number }> {
    const { data } = await http.post<{ requested_at: string; count: number }>(`/operator/customers/${customerId}/citation-research-request`, { citations });
    return data;
  },

  /** What releasing shows the practice, and who is emailed (UXWALK P1-8). */
  async releasePreview(customerId: string): Promise<ReleasePreview> {
    const { data } = await http.get<ReleasePreview>(`/operator/customers/${customerId}/results/release-preview`);
    return data;
  },

  // ---- Practice groups (W4, UX-REDESIGN-BRIEF.md Addendum) -----------------
  async practiceGroup(customerId: string): Promise<OperatorPracticeGroup> {
    const { data } = await http.get<OperatorPracticeGroup>(
      `/operator/customers/${customerId}/practice-group`,
    );
    return data;
  },

  async linkPracticeGroup(
    customerId: string,
    otherCustomerId: string,
  ): Promise<OperatorPracticeGroup> {
    const { data } = await http.post<OperatorPracticeGroup>(
      `/operator/customers/${customerId}/practice-group/link`,
      { other_customer_id: otherCustomerId },
    );
    return data;
  },

  // This team queue is mounted at /v1/operator, outside the dashboard API's
  // usual /v1/dash prefix. Override only the base URL so auth stays attached.
  async partRequests(): Promise<OperatorPartRequest[]> {
    const { data } = await http.get<unknown>(
      '/operator/practice-groups/part-requests',
      { baseURL: API_BASE.replace(/\/dash\/?$/, '') },
    );
    // The server answers {"requests": [...]} with `id`, `customer_vertical`
    // and `resolved_at`; tolerate a bare array or an unexpected body.
    const rows: unknown = Array.isArray(data)
      ? data
      : (data as { requests?: unknown } | null)?.requests;
    if (!Array.isArray(rows)) return [];
    return rows.map((r: Record<string, unknown>) => ({
      request_id: String(r.request_id ?? r.id ?? ''),
      customer_id: String(r.customer_id ?? ''),
      customer_name: String(r.customer_name ?? ''),
      vertical: String(r.vertical ?? r.customer_vertical ?? ''),
      requested_vertical: String(r.requested_vertical ?? ''),
      requested_at: String(r.requested_at ?? ''),
      requested_by_email: (r.requested_by_email as string | null | undefined) ?? null,
      resolved: r.resolved !== undefined ? Boolean(r.resolved) : Boolean(r.resolved_at),
      resolved_at: (r.resolved_at as string | null | undefined) ?? null,
    }));
  },

  async resolvePartRequest(requestId: string): Promise<OperatorPartRequest> {
    const { data } = await http.post<OperatorPartRequest>(
      `/operator/practice-groups/part-requests/${requestId}/resolve`,
      undefined,
      { baseURL: API_BASE.replace(/\/dash\/?$/, '') },
    );
    return data;
  },

  /** The practice's intake and the compliance rows it sees, as one JSON
   *  document the team builds the counsel binder from OFFLINE (BL-383).
   *  Med-spa workspaces only; audited server-side. */
  async portalBundle(customerId: string): Promise<unknown> {
    const { data } = await http.get<unknown>(`/operator/customers/${customerId}/portal-bundle`);
    return data;
  },

  async coverage(): Promise<StateCoverage[]> {
    const { data } = await http.get<StateCoverage[]>('/operator/coverage');
    return data;
  },

  async sitePreview(website: string, signal?: AbortSignal): Promise<SitePreview> {
    const { data } = await http.post<SitePreview>('/operator/site-preview', { website }, { signal });
    return data;
  },

  async createWorkspace(
    body: CreateWorkspaceInput,
  ): Promise<{ customer_id: string; states: string[] }> {
    const { data } = await http.post<{ customer_id: string; states: string[] }>(
      '/operator/customers',
      body,
    );
    return data;
  },

  async patchWorkspace(
    customerId: string,
    body: {
      name?: string;
      website?: string | null;
      is_evaluation?: boolean;
      status?: 'active' | 'archived';
      practice_seat_cap?: number;
      auto_release_enabled?: boolean;
      counsel_review_mode?: 'every_report' | 'when_chosen' | 'not_used';
    },
  ): Promise<void> {
    await http.patch(`/operator/customers/${customerId}`, body);
  },

  async workspaceFeatures(customerId: string): Promise<FeatureSwitch[]> {
    const { data } = await http.get<{ features: FeatureSwitch[] }>(
      `/operator/customers/${customerId}/features`,
    );
    return data.features;
  },

  async switchFeature(
    customerId: string,
    key: string,
    enabled: boolean,
    reason: string,
  ): Promise<FeatureSwitch[]> {
    const { data } = await http.put<{ features: FeatureSwitch[] }>(
      `/operator/customers/${customerId}/features/${key}`,
      { enabled, reason },
    );
    return data.features;
  },

  async releaseResults(customerId: string): Promise<{ sent: number; reason: string | null } | null> {
    const { data } = await http.post<{ email?: { sent: number; reason: string | null } | null }>(
      `/operator/customers/${customerId}/results/release`,
      { opened_where_you_stand: true, checked_questions: true },
    );
    return data?.email ?? null;
  },

  async holdResults(customerId: string): Promise<void> {
    await http.post(`/operator/customers/${customerId}/results/hold`);
  },

  async openStates(customerId: string, states: string[]): Promise<void> {
    await http.post(`/operator/customers/${customerId}/states`, { states });
  },

  async withdrawState(customerId: string, state: string): Promise<void> {
    await http.post(`/operator/customers/${customerId}/states/withdraw`, { state });
  },

  // The ops surface's one sanctioned deletion: wipes a med-spa workspace's
  // intake (never its grants, states or review), against a typed confirmation
  // of the exact customer name.
  async resetWorkspace(customerId: string, confirm: string): Promise<void> {
    await http.post(`/ops/portal/intake-reset/${customerId}`, { confirm });
  },

  // ---- Files a practice hands us (BL-350) ----------------------------------
  async listUploads(customerId: string): Promise<UploadsTeamList> {
    const { data } = await http.get<UploadsTeamList>(
      `/operator/customers/${customerId}/uploads`,
    );
    return data;
  },
  async uploadReport(customerId: string, uploadId: string): Promise<UploadReport> {
    const { data } = await http.get<UploadReport>(
      `/operator/customers/${customerId}/uploads/${uploadId}/report`,
    );
    return data;
  },
  async setUploadMapping(
    customerId: string,
    uploadId: string,
    mapping: Record<string, string>,
  ): Promise<UploadTeamRow> {
    const { data } = await http.post<UploadTeamRow>(
      `/operator/customers/${customerId}/uploads/${uploadId}/mapping`,
      { mapping },
    );
    return data;
  },
  async uploadFactsUrl(customerId: string, uploadId: string): Promise<{ url: string }> {
    const { data } = await http.get<{ url: string }>(
      `/operator/customers/${customerId}/uploads/${uploadId}/facts-url`,
    );
    return data;
  },
  async uploadRawUrl(
    customerId: string,
    uploadId: string,
    reason: string,
  ): Promise<{ url: string }> {
    const { data } = await http.post<{ url: string }>(
      `/operator/customers/${customerId}/uploads/${uploadId}/raw-url`,
      { reason },
    );
    return data;
  },
  async deleteUpload(customerId: string, uploadId: string): Promise<void> {
    await http.delete(`/operator/customers/${customerId}/uploads/${uploadId}`);
  },

  // ---- Declared data sources, team status (R4/R6) ----------------------------
  async dataSourceStatus(customerId: string): Promise<DataSourceStatusList> {
    const { data } = await http.get<DataSourceStatusList>(
      `/operator/customers/${customerId}/data-sources`,
    );
    return data;
  },
  async stillNeededOperator(customerId: string): Promise<StillNeededOperatorList> {
    const { data } = await http.get<StillNeededOperatorList>(
      `/operator/customers/${customerId}/still-needed`,
    );
    return data;
  },
  async setDataSourceFreshness(customerId: string, days: number): Promise<{ freshness_window_days: number }> {
    const { data } = await http.patch<{ freshness_window_days: number }>(
      `/operator/customers/${customerId}/data-sources/freshness`,
      { days },
    );
    return data;
  },
  async linkUploadSource(
    customerId: string,
    uploadId: string,
    sourceId: string | null,
  ): Promise<{ upload_id: string; data_source_id: string | null }> {
    const { data } = await http.post<{ upload_id: string; data_source_id: string | null }>(
      `/operator/customers/${customerId}/uploads/${uploadId}/link-source`,
      { source_id: sourceId },
    );
    return data;
  },
  async reopenDataSourceNeed(customerId: string, itemKey: string): Promise<{ item_key: string; reopened: boolean }> {
    const { data } = await http.post<{ item_key: string; reopened: boolean }>(
      `/operator/customers/${customerId}/data-sources/needs/${encodeURIComponent(itemKey)}/reopen`,
    );
    return data;
  },

  // ---- Cost calculator (BL-151) ---------------------------------------------
  async costDefaults(customerId: string): Promise<CostDefaults> {
    const { data } = await http.get<CostDefaults>(
      `/customers/${customerId}/cost/defaults`,
    );
    return data;
  },

  async costEstimate(
    customerId: string,
    body: CostEstimateInput,
  ): Promise<CostEstimate> {
    const { data } = await http.post<CostEstimate>(
      `/customers/${customerId}/cost/estimate`,
      body,
    );
    return data;
  },


  // ---- Practice source notices (BL-562) ------------------------------------
  // A sibling of the binder review, and reachable without one: these need only
  // the grant. Every write is idempotent on a `request_key` the caller mints,
  // and every write returns a HISTORICAL receipt — so a caller refetches
  // rather than treating the response as the current standing.
  async notices(
    customerId: string,
    params: { limit?: number; cursor?: string } = {},
  ): Promise<NoticeQueuePage> {
    const { data } = await http.get<NoticeQueuePage>(
      `/customers/${customerId}/notices`,
      { params },
    );
    return data;
  },

  async notice(customerId: string, noticeId: string): Promise<NoticeDetailView> {
    const { data } = await http.get<NoticeDetailView>(
      `/customers/${customerId}/notices/${noticeId}`,
    );
    return data;
  },

  /**
   * One page of source events a notice could be prepared for.
   *
   * A page, not a shortlist: every row read is returned, and `next_cursor`
   * walks to older ones. Still never permission — compose revalidates.
   */
  async noticeCandidates(
    customerId: string,
    params: { limit?: number; cursor?: string } = {},
  ): Promise<NoticeCandidateList> {
    const { data } = await http.get<NoticeCandidateList>(
      `/customers/${customerId}/notices/candidates`,
      { params },
    );
    return data;
  },

  async composeNotice(
    customerId: string,
    body: {
      origin_event_id: string;
      staff_note?: string | null;
      interpretation?: string | null;
      request_key: string;
    },
  ): Promise<NoticeReceipt> {
    const { data } = await http.post<NoticeReceipt>(
      `/customers/${customerId}/notices/compose`,
      body,
    );
    return data;
  },

  async amendNotice(
    customerId: string,
    noticeId: string,
    body: {
      staff_note?: string | null;
      interpretation?: string | null;
      expected_version: number;
      expected_content_hash: string;
      request_key: string;
    },
  ): Promise<NoticeReceipt> {
    const { data } = await http.post<NoticeReceipt>(
      `/customers/${customerId}/notices/${noticeId}/amend`,
      body,
    );
    return data;
  },

  async decideNotice(
    customerId: string,
    noticeId: string,
    body: {
      decision: NoticeDecisionKind;
      reason?: string | null;
      expected_version: number;
      expected_content_hash: string;
      expected_decision_revision: number;
      request_key: string;
    },
  ): Promise<NoticeReceipt> {
    const { data } = await http.post<NoticeReceipt>(
      `/customers/${customerId}/notices/${noticeId}/decision`,
      body,
    );
    return data;
  },

  // ---- Current actor --------------------------------------------------------
  // Who am I + my role. The prod Cognito access token carries no app role, so the
  // SPA calls this after sign-in to decide admin vs attorney UI.
  async me(): Promise<{ user_id: string; role: string; is_internal_admin: boolean }> {
    const { data } = await http.get<{
      user_id: string;
      role: string;
      is_internal_admin: boolean;
    }>('/me');
    return data;
  },
};

export type Api = typeof api;

// ---- Research publishing (internal-admin only; spec §5) --------------------
// Import a library document, acknowledge its review, publish it, withdraw
// it. The import sends the file's text as the JSON body: the server streams
// and bounds it (12 MB) and validates the closed shape itself.
export const researchAdminApi = {
  async publications(): Promise<ResearchPublicationHistory> {
    const { data } = await http.get<ResearchPublicationHistory>('/operator/research/publications');
    return data;
  },
  async drafts(): Promise<{ drafts: ResearchDraftRow[]; next: string | null }> {
    const { data } = await http.get<{ drafts: ResearchDraftRow[]; next: string | null }>(
      '/operator/research/drafts',
    );
    return data;
  },
  async draft(draftId: string): Promise<ResearchDraftDetail> {
    const { data } = await http.get<ResearchDraftDetail>(`/operator/research/drafts/${draftId}`);
    return data;
  },
  async importDraft(fileText: string): Promise<ResearchImportResult> {
    const { data } = await http.post<ResearchImportResult>('/operator/research/drafts', fileText, {
      headers: { 'Content-Type': 'application/json' },
      // The server answers 422 for a document that does not fit; the text is
      // sent as-is so a formatting difference cannot change its hash.
      transformRequest: [(body: string) => body],
    });
    return data;
  },
  async review(
    draftId: string,
    body: { content_hash: string; method: 'human' | 'ai_assisted'; note: string; request_key: string },
  ): Promise<ResearchDraftReview> {
    const { data } = await http.post<ResearchDraftReview>(`/operator/research/drafts/${draftId}/review`, body);
    return data;
  },
  async publish(
    draftId: string,
    body: { review_id: string; expected_current: string | null; reason: string },
  ): Promise<{ id: string; version: string; status: string }> {
    const { data } = await http.post<{ id: string; version: string; status: string }>(
      `/operator/research/drafts/${draftId}/publish`,
      body,
    );
    return data;
  },
  async withdraw(
    publicationId: string,
    body: { reason: string; expected_status: 'published' | 'superseded' },
  ): Promise<{ id: string; status: string }> {
    const { data } = await http.post<{ id: string; status: string }>(
      `/operator/research/publications/${publicationId}/withdraw`,
      body,
    );
    return data;
  },
  /** Every customer's research requests waiting on the team (BL-614).
   *  Read-only: acting on a row goes through the workspace's own endpoint
   *  (portalApi.researchCaseAction), so there is one write path. */
  async customerRequests(params?: {
    status?: 'open' | 'all';
    cursor?: string | null;
    limit?: number;
  }): Promise<ResearchCustomerRequestQueue> {
    const { data } = await http.get<ResearchCustomerRequestQueue>('/operator/research/requests', {
      params: {
        status: params?.status ?? 'open',
        ...(params?.cursor ? { cursor: params.cursor } : {}),
        ...(params?.limit ? { limit: params.limit } : {}),
      },
    });
    return data;
  },
  /** The queue of recorded source changes that still need a person. */
  async sourceChanges(before?: number): Promise<ResearchSourceChangeQueue> {
    const { data } = await http.get<ResearchSourceChangeQueue>('/operator/research/source-changes', {
      params: before ? { before } : undefined,
    });
    return data;
  },
  async disposition(
    checkId: number,
    body: { disposition: ResearchDisposition; publication_id?: string | null },
  ): Promise<{ id: number; disposition: string; reviewed_at: string }> {
    const { data } = await http.post<{ id: number; disposition: string; reviewed_at: string }>(
      `/operator/research/source-changes/${checkId}/disposition`,
      body,
    );
    return data;
  },
};
